import { useReduxState } from 'hooks';
import {
  AchievementRenderAddedBy,
  AchievementSimpleStatus
} from 'components/achievements';
import {
  ContentLabel,
  ContentValue,
  DisplayAttachments,
  DisplayFormattedDate,
  FlexContainer,
  TextWithLineBreaks,
  User
} from 'components/common';
import { Box, BoxBackLink } from 'components/common/box';
import { AttributeDisplayValue } from 'components/common/customAttributes';

import type { GetUserAchievementsRes } from 'shared-types/responses';

interface Props {
  achievement: Pick<
    GetUserAchievementsRes['achievements'][0],
    'certificateAllowDownloading' | 'type' | 'userAttributes'
  >;
  completion: GetUserAchievementsRes['achievements'][0]['completions'][0];
  loading: boolean;
  onBack?: () => void;
}

const ProfileAchievementsAchievementProgress = ({
  achievement,
  completion,
  loading,
  onBack
}: Props) => {
  const { language, messages, theme } = useReduxState();

  return (
    <div className="progress">
      <Box
        loading={loading}
        padding={20}
        title={
          <>
            {onBack ? (
              <BoxBackLink
                onBackClick={onBack}
                simpleReturn={true}
                text={messages.commonProgress}
              />
            ) : (
              messages.commonProgress
            )}
          </>
        }
      >
        {completion ? (
          <div className="inner">
            {completion.members?.length ? (
              <ContentLabel
                horizontal={true}
                label={messages.commonParticipants}
              >
                <FlexContainer style={{ gap: 10 }}>
                  {completion?.members.map((member) => (
                    <User key={member.id} size="small" user={member} />
                  ))}
                </FlexContainer>
              </ContentLabel>
            ) : null}

            <ContentLabel horizontal={true} label={messages.commonStatus}>
              <AchievementSimpleStatus
                expirationDate={completion?.expirationDate}
                isInactive={completion?.isInactive}
                status={completion?.status}
                type={achievement.type}
              />
            </ContentLabel>

            {completion.startedAtDate ? (
              <ContentLabel
                horizontal={true}
                label={messages.commonStartDate}
                style={{ whiteSpace: 'nowrap' }}
              >
                <ContentValue style={{ fontWeight: 600 }}>
                  <DisplayFormattedDate date={completion.startedAtDate} />
                </ContentValue>
              </ContentLabel>
            ) : null}

            {completion.status === 'completed' ? (
              <ContentLabel
                horizontal={true}
                label={messages.completionsCompletionDate}
                style={{ whiteSpace: 'nowrap' }}
              >
                <ContentValue style={{ fontWeight: 600 }}>
                  <DisplayFormattedDate date={completion.completionDate} />
                </ContentValue>
              </ContentLabel>
            ) : null}

            {completion.expirationDate ? (
              <ContentLabel
                horizontal={true}
                label={messages.commonExpirationDate}
                style={{ whiteSpace: 'nowrap' }}
              >
                <ContentValue style={{ fontWeight: 600 }}>
                  <DisplayFormattedDate date={completion.expirationDate} />
                </ContentValue>
              </ContentLabel>
            ) : null}

            <ContentLabel horizontal={true} label={messages.commonAddedBy}>
              <ContentValue style={{ fontWeight: 600 }}>
                <AchievementRenderAddedBy
                  addedBy={completion.addedBy}
                  enableLink={true}
                />
              </ContentValue>
            </ContentLabel>

            {completion.comments ? (
              <ContentLabel horizontal={true} label={messages.commonComment}>
                <div className="comment">
                  <TextWithLineBreaks text={completion.comments} />
                </div>
              </ContentLabel>
            ) : null}

            {achievement.certificateAllowDownloading ? (
              <ContentLabel
                horizontal={true}
                label={messages.commonAttachments}
              >
                <DisplayAttachments
                  attachments={completion.attachments}
                  isConfidential={!completion.canSeeAttachments}
                  renderInPortal={false}
                />
              </ContentLabel>
            ) : null}

            {achievement.userAttributes?.length
              ? achievement.userAttributes.map((attribute, index) => (
                  <ContentLabel
                    horizontal={true}
                    key={index}
                    label={
                      attribute.titleTranslations?.[language] || attribute.title
                    }
                  >
                    <ContentValue style={{ fontWeight: 600 }}>
                      <AttributeDisplayValue
                        value={completion?.userAttributes?.[attribute.id]}
                      />
                    </ContentValue>
                  </ContentLabel>
                ))
              : null}
          </div>
        ) : null}
      </Box>

      <style jsx>{`
        .comment {
          background-color: ${theme.lightGreyLighter};
          border-radius: ${theme.cardBorderRadius};
          font-size: 1.4rem;
          font-weight: 400;
          line-height: 1.5;
          padding: 8px 12px;
        }
      `}</style>
    </div>
  );
};

export default ProfileAchievementsAchievementProgress;
